<template>
  <expansion-panels
    v-if="hasReferences"
    v-model="expanded"
    :items="references"
    value-prop="id"
    multiple
    keep-activator
  >
    <template #activator="{ item }">
      <reference-card-collapsed
        :reference="item"
        :highlight-terms="getHighlightTerms(item)"
        @add-abstract="
          $emit('edit', { reference: item, focus: 'abstract' })
        "
      />
    </template>
    <template #content="{ item, toggle, isActive }">
      <reference-card-expanded
        :review="review"
        :reference="item"
        :researcher="researcher"
        :expanded="isActive"
        :highlight-terms="getHighlightTerms(item)"
        :disable-actions="
          disableActionsForReferences.includes(item.id)
        "
        @collapse="toggle"
        @edit="$emit('edit', { reference: item, focus: $event })"
        @download-full-text="$emit('download-full-text', item)"
        @delete-full-text="$emit('delete-full-text', item)"
        @update-tags="
          $emit('set-reference-tags', {
            reference: item,
            tags: $event,
          })
        "
        @create-screening-vote="
          $emit('create-screening-vote', $event)
        "
        @change-screening-vote="
          $emit('change-screening-vote', $event)
        "
        @revoke-screening-vote="
          $emit('revoke-screening-vote', $event)
        "
        @resolve-screening-conflict="
          $emit('resolve-screening-conflict', $event)
        "
        @change-screening-outcome="
          $emit('change-screening-outcome', $event)
        "
        @create-full-text-review-vote="
          $emit('create-full-text-review-vote', $event)
        "
        @change-full-text-review-vote="
          $emit('change-full-text-review-vote', $event)
        "
        @revoke-full-text-review-vote="
          $emit('revoke-full-text-review-vote', $event)
        "
        @resolve-full-text-review-conflict="
          $emit('resolve-full-text-review-conflict', $event)
        "
        @change-full-text-review-outcome="
          $emit('change-full-text-review-outcome', $event)
        "
        @extract="$emit('extract', $event)"
        @extraction-consensus-review="
          $emit('extraction-consensus-review', $event)
        "
        @mark-as-duplicate="$emit('mark-as-duplicate', $event)"
      />
    </template>
  </expansion-panels>
</template>

<script>
import ExpansionPanels from '@/components/Common/ExpansionPanels';
import ReferenceCardCollapsed from './ReferenceCardCollapsed';
import ReferenceCardExpanded from './ReferenceCardExpanded';
import {
  Reference,
  Review,
  Researcher,
  ReferenceTermHighlights,
} from '@/models';
import { typeHelpers } from '@/modules';

export default {
  name: 'ReferenceCard',
  components: {
    ExpansionPanels,
    ReferenceCardCollapsed,
    ReferenceCardExpanded,
  },
  props: {
    references: {
      type: Array,
      required: true,
      validator: (val) => typeHelpers.isArrayOfType(val, Reference),
    },
    highlightTerms: {
      type: Array,
      default: () => [],
      validator: (val) =>
        typeHelpers.isArrayOfType(val, ReferenceTermHighlights),
    },
    review: {
      type: Review,
      required: true,
    },
    researcher: {
      type: Researcher,
      required: true,
    },
    disableActionsForReferences: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'edit',
    'download-full-text',
    'delete-full-text',
    'set-reference-tags',
    'create-screening-vote',
    'change-screening-vote',
    'revoke-screening-vote',
    'resolve-screening-conflict',
    'change-screening-outcome',
    'create-full-text-review-vote',
    'change-full-text-review-vote',
    'revoke-full-text-review-vote',
    'resolve-full-text-review-conflict',
    'change-full-text-review-outcome',
    'extract',
    'extraction-consensus-review',
    'mark-as-duplicate',
  ],
  data: () => ({ expanded: [] }),
  computed: {
    hasReferences() {
      return this.references.length > 0;
    },
  },
  methods: {
    getHighlightTerms(reference) {
      const highlight = this.highlightTerms?.find(
        (h) => h.referenceId === reference.id
      );
      return highlight ? highlight.matches : [];
    },
  },
};
</script>
